import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import MusicNoteOutlinedIcon from '@mui/icons-material/MusicNoteOutlined';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import { format, Locale } from 'date-fns';
import { enGB, pl as plPL } from 'date-fns/locale';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { hostApiClient } from '../../hostApiClient';
import { PassInstanceStatus, PassProviderInstanceSummary } from '../../models/Pass';
import { passQuantityLabel, passUsedQuantity } from '../../utils/passFormatting';
import {
  canActivatePassPurchase,
  isActiveSoldPassTabStatus,
  isPendingCashPassPurchase,
} from '../../utils/passPurchaseCollection';
import RoomBookingUserAvatar from '../RoomBookingUserAvatar';
import PassInstanceStatusChip from './PassInstanceStatusChip';
import { PASS_IDENTITY_AVATAR_SIZE } from './passDetailLayout';

const dateLocales: { [key: string]: Locale } = {
  en: enGB,
  pl: plPL,
};

const HISTORY_STATUSES: PassInstanceStatus[] = ['EXPIRED', 'EXHAUSTED', 'CANCELLED'];

type SoldTab = 'active' | 'history';

export interface PassOfferInstancesPanelProps {
  passId: string;
}

/** Sold instances for one host pass offer, split Active / History like My Passes. */
const PassOfferInstancesPanel: React.FC<PassOfferInstancesPanelProps> = ({ passId }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dateLocale = dateLocales[i18n.language] || enGB;
  const [instances, setInstances] = useState<PassProviderInstanceSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [soldTab, setSoldTab] = useState<SoldTab>('active');
  const [actionTarget, setActionTarget] = useState<PassProviderInstanceSummary | null>(null);
  const [busyPurchaseId, setBusyPurchaseId] = useState<string | null>(null);

  const loadInstances = useCallback(async (options?: { silent?: boolean }) => {
    try {
      if (!options?.silent) {
        setLoading(true);
      }
      setError(null);
      const data = await hostApiClient.listPassOfferInstances(passId);
      setInstances(data);
    } catch (err) {
      console.error(`Error loading pass offer instances ${passId}:`, err);
      setError(t('hostPasses.instancesFetchError', 'Failed to load sold passes'));
    } finally {
      if (!options?.silent) {
        setLoading(false);
      }
    }
  }, [passId, t]);

  useEffect(() => {
    void loadInstances();
  }, [loadInstances]);

  const formatPurchasedAt = (value?: string | null) => {
    if (!value) return '—';
    try {
      return format(new Date(value), 'PP', { locale: dateLocale });
    } catch {
      return new Date(value).toLocaleDateString(i18n.language);
    }
  };

  const visibleInstances = useMemo(() => {
    if (soldTab === 'active') {
      return instances.filter((instance) => isActiveSoldPassTabStatus(instance.status));
    }
    return instances.filter((instance) => HISTORY_STATUSES.includes(instance.status));
  }, [instances, soldTab]);

  const openActionDialog = (instance: PassProviderInstanceSummary, event: React.MouseEvent) => {
    event.stopPropagation();
    if (!canActivatePassPurchase(instance)) return;
    setActionTarget(instance);
  };

  const closeActionDialog = () => {
    if (busyPurchaseId) return;
    setActionTarget(null);
  };

  const handleActionSubmit = async () => {
    const purchaseId = actionTarget?.passPurchaseId?.trim();
    if (!purchaseId || !actionTarget || !canActivatePassPurchase(actionTarget)) return;

    setBusyPurchaseId(purchaseId);
    setError(null);
    try {
      await hostApiClient.activatePassPurchase(purchaseId);
      setActionTarget(null);
      await loadInstances({ silent: true });
    } catch (err) {
      console.error(`Error activating pass purchase ${purchaseId}:`, err);
      setError(t('hostPasses.activateError', 'Failed to activate pass'));
    } finally {
      setBusyPurchaseId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (error && instances.length === 0) {
    return (
      <Alert severity="error" sx={{ my: 0.5 }}>
        {error}
      </Alert>
    );
  }

  const emptyMessage =
    instances.length === 0
      ? t('hostPasses.instancesEmpty', 'No sold passes yet')
      : soldTab === 'active'
        ? t('hostPasses.instancesActiveEmpty', 'No active sold passes')
        : t('hostPasses.instancesHistoryEmpty', 'No past sold passes');

  const dialogBusy = Boolean(busyPurchaseId);

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 1.25, letterSpacing: '-0.01em' }}>
        {t('hostPasses.instancesTitle', 'Sold passes')}
      </Typography>
      {error ? (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      {instances.length > 0 ? (
        <Tabs
          value={soldTab}
          onChange={(_event, value: SoldTab) => setSoldTab(value)}
          sx={{ mb: 1.25, minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0 } }}
        >
          <Tab value="active" label={t('hostPasses.instancesTabActive', 'Active')} />
          <Tab value="history" label={t('hostPasses.instancesTabHistory', 'History')} />
        </Tabs>
      ) : null}
      {visibleInstances.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
          {emptyMessage}
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gap: 0.75,
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(3, minmax(0, 1fr))',
            },
          }}
        >
          {visibleInstances.map((instance) => {
            const buyerName = instance.buyer?.name?.trim() || null;
            const buyerBand = instance.buyer?.bandName?.trim() || null;
            const pendingCash = isPendingCashPassPurchase(instance);
            const showActivate = canActivatePassPurchase(instance);
            const rowBusy = busyPurchaseId === instance.passPurchaseId;
            const openDetails = () => {
              if (pendingCash) return;
              navigate(`/host/passes/instances/${instance.id}`);
            };
            return (
              <Box
                key={instance.id}
                role={pendingCash ? undefined : 'button'}
                tabIndex={pendingCash ? undefined : 0}
                onClick={openDetails}
                onKeyDown={(event) => {
                  if (pendingCash) return;
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openDetails();
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  px: 1,
                  py: 0.75,
                  minWidth: 0,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: pendingCash || showActivate ? 'info.light' : 'divider',
                  bgcolor: 'action.hover',
                  cursor: pendingCash ? 'default' : 'pointer',
                  transition: 'background-color 0.15s ease, border-color 0.15s ease',
                  ...(!pendingCash
                    ? {
                        '&:hover': {
                          bgcolor: 'action.selected',
                          borderColor: 'text.disabled',
                        },
                      }
                    : null),
                }}
              >
                <RoomBookingUserAvatar
                  userProfileImageUrl={instance.buyer?.profileImageUrl}
                  displayName={buyerName || ''}
                  size={PASS_IDENTITY_AVATAR_SIZE}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1, lineHeight: 1.25 }}>
                      {buyerName || t('userPasses.details.unknownBuyer', 'Pass owner')}
                    </Typography>
                    <PassInstanceStatusChip status={instance.status} />
                    {showActivate ? (
                      <Tooltip title={t('hostPasses.activate', 'Activate')}>
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            aria-label={t('hostPasses.activate', 'Activate')}
                            disabled={rowBusy}
                            onClick={(event) => openActionDialog(instance, event)}
                          >
                            {rowBusy ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <TaskAltRoundedIcon sx={{ fontSize: 18 }} />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : null}
                  </Box>
                  {buyerBand ? (
                    <Stack direction="row" spacing={0.25} alignItems="center" useFlexGap sx={{ mt: 0.15, minWidth: 0 }}>
                      <MusicNoteOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
                      <Typography variant="caption" color="text.secondary" component="span" noWrap sx={{ lineHeight: 1.25 }}>
                        <Trans
                          i18nKey="userPasses.details.playsAt"
                          defaults="Plays at <bandName>{{name}}</bandName>"
                          values={{ name: buyerBand }}
                          components={{
                            bandName: (
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                                fontWeight={600}
                              />
                            ),
                          }}
                        />
                      </Typography>
                    </Stack>
                  ) : null}
                  <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ lineHeight: 1.3 }}>
                    {[
                      passQuantityLabel(
                        passUsedQuantity(instance.remainingQuantity, instance.totalQuantity),
                        instance.totalQuantity,
                        instance.unitType,
                        t,
                      ),
                      formatPurchasedAt(instance.purchasedAt),
                    ].join(' · ')}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      <Dialog open={Boolean(actionTarget)} onClose={closeActionDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{t('hostPasses.activateDialogTitle', 'Activate pass')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t(
              'hostPasses.activateDialogDescription',
              'Issue this pass to the buyer and mark the on-site cash payment as collected.',
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeActionDialog} disabled={dialogBusy}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleActionSubmit}
            disabled={dialogBusy}
            startIcon={dialogBusy ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {t('hostPasses.activate', 'Activate')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PassOfferInstancesPanel;
